import React, { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import { getAdminProjects } from 'ServerAPI'
import Button from 'components/base/Button'
import Input from 'components/base/Input'
import Select from 'components/base/Select'
import MenuItem from 'components/base/MenuItem'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import { formatUnixTime } from '~/components/admin/formatUnixTime'
import { usePageSize } from '~/components/admin/usePageSize'
import { getTruncatedValue } from '~/components/admin/ValuePopup'
import { AppRoutes } from '~/AppRoutes'
import './RecentProjects.scss'

const getNameValue = name => {
  if (name === null || name === undefined) return name
  return (
    <span className="admin-list__table__cell-text" title={String(name)}>
      {getTruncatedValue(name)}
    </span>
  )
}

export const RecentProjects = () => {
  const { pageSize, tableRef, isReady } = usePageSize()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const sortByFromQuery = queryParams.get('sortBy')
  const searchFromQuery = queryParams.get('search')
  const [ searchText, setSearchText ] = useState(searchFromQuery || '')
  const [ search, setSearch ] = useState(searchFromQuery || '')
  const [ searchBy, setSearchBy ] = useState(sortByFromQuery === 'userId' ? 'userId' : 'name')
  const [ start, setStart ] = useState(0)
  const [ projects, setProjects ] = useState([])
  const [ totalCount, setTotalCount ] = useState(0)
  const [ loading, setLoading ] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAdminProjects({
        start_number: start,
        count: pageSize,
        search,
        searchBy,
      })
      setProjects(data.entries || [])
      setTotalCount(data.total_count || 0)
    } catch (err) {
      setProjects([])
      setTotalCount(0)
      // eslint-disable-next-line no-console
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [ start, search, searchBy, pageSize ])

  useEffect(() => {
    if (!isReady) return
    load()
  }, [ load, isReady ])

  const onSearch = () => {
    setStart(0)
    setSearch(searchText)
  }

  const onPage = diff => {
    const nextStart = start + diff * pageSize
    if (nextStart < 0 || nextStart >= totalCount) return
    setStart(nextStart)
  }

  const currentPage = totalCount ? Math.floor(start / pageSize) + 1 : 0
  const totalPages = Math.ceil(totalCount / pageSize)

  return (
    <div className="admin-list">
      <div className="admin-list__search">
        <span className="admin-list__search__label">Search by</span>
        <Select
          className="admin-list__search__select admin-list__search__select--projects"
          dropdownClassName="admin-list__search__dropdown--projects"
          value={searchBy}
          onChange={e => {
            setSearchBy(e.target.value)
            setStart(0)
          }}
        >
          <MenuItem value="id">id</MenuItem>
          <MenuItem value="name">name</MenuItem>
          <MenuItem value="userId">userId</MenuItem>
          <MenuItem value="secreteAPIKey">secreteAPIKey</MenuItem>
        </Select>
        <Input
          id="admin-projects-search"
          className="admin-list__search__input"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onEnterKeyDown={onSearch}
        />
        <Button className="admin-list__search__button" primary onClick={onSearch}>
          Search
        </Button>
      </div>

      <div className="admin-list__table" ref={tableRef}>
        <Table aria-label="recent projects">
          <TableHead>
            <TableRow>
              <TableCell>id</TableCell>
              <TableCell className="admin-list__table__name">name</TableCell>
              <TableCell>userId</TableCell>
              <TableCell>createTime</TableCell>
              <TableCell>modifTime</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project, index) => (
              <TableRow key={project.id || index}>
                <TableCell>
                  <a
                    className="admin-panel__link"
                    href={`${window.location.origin}/projects/${project.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {project.id}
                  </a>
                </TableCell>
                <TableCell className="admin-list__table__name">
                  {getNameValue(project.name)}
                </TableCell>
                <TableCell>
                  <Link
                    className="admin-panel__link"
                    to={`${AppRoutes.AdminRecentUsers}/${project.userId}`}
                  >
                    {project.userId}
                  </Link>
                </TableCell>
                <TableCell>{formatUnixTime(project.createTime)}</TableCell>
                <TableCell>{formatUnixTime(project.modifTime)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="admin-list__footer">
        <If condition={loading}>
          <CircularProgress endless percents={80} transparent size={24} />
        </If>
        <div className="admin-list__footer__nav">
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(-1)}
            disabled={start === 0}
          >
            Prev
          </Button>
          <Button
            className="admin-list__footer__nav__btn"
            secondary
            onClick={() => onPage(1)}
            disabled={totalCount === 0 || start + pageSize >= totalCount}
          >
            Next
          </Button>
        </div>
        <span className="admin-list__footer__info">
          {'page '}
          {String(currentPage)}
          {' of '}
          {String(totalPages)}
          {', totalCount: '}
          {String(totalCount)}
        </span>
      </div>
    </div>
  )
}
